import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import QuizComponent from '../../components/QuizComponent';
import { BookOpen, CheckCircle, Heart, Shield, Zap, Lock, Cross } from 'lucide-react';

// The Gospel Introduction - What Saves From What
const gospelIntro = {
  question: "What must I do to be saved?",
  reference: "Acts 16:30",
  answer: "Believe in the Lord Jesus, and you will be saved.",
  answerRef: "Acts 16:31",
  
  sections: [
    {
      title: "Saved FROM What?",
      points: [
        { text: "From God's wrath against sin", verse: "Jesus... delivers us from the wrath to come.", ref: "1 Thessalonians 1:10" },
        { text: "From eternal death and separation", verse: "The wages of sin is death, but the free gift of God is eternal life in Christ Jesus.", ref: "Romans 6:23" },
        { text: "From the penalty we deserve", verse: "He himself bore our sins in his body on the tree.", ref: "1 Peter 2:24" },
        { text: "From slavery to sin", verse: "Everyone who practices sin is a slave to sin... if the Son sets you free, you will be free indeed.", ref: "John 8:34, 36" },
      ]
    },
    {
      title: "Saved BY What?",
      points: [
        { text: "By grace alone—God's unearned favor", verse: "For by grace you have been saved through faith. And this is not your own doing; it is the gift of God.", ref: "Ephesians 2:8" },
        { text: "Through faith alone—simply believing", verse: "Whoever believes in him is not condemned.", ref: "John 3:18" },
        { text: "In Christ alone—His finished work", verse: "There is salvation in no one else, for there is no other name under heaven given among men by which we must be saved.", ref: "Acts 4:12" },
        { text: "NOT by works—nothing you do", verse: "Not a result of works, so that no one may boast.", ref: "Ephesians 2:9" },
      ]
    },
    {
      title: "Saved TO What?",
      points: [
        { text: "To eternal life—starting now", verse: "Whoever believes in the Son has eternal life.", ref: "John 3:36" },
        { text: "To union with Christ—one with Him", verse: "He who is joined to the Lord becomes one spirit with him.", ref: "1 Corinthians 6:17" },
        { text: "To complete forgiveness—all sins dealt with", verse: "In him we have redemption through his blood, the forgiveness of our trespasses.", ref: "Ephesians 1:7" },
        { text: "To a new identity—born again, new creation", verse: "If anyone is in Christ, he is a new creation.", ref: "2 Corinthians 5:17" },
      ]
    }
  ],
  
  notByWorks: [
    "Not by being baptized",
    "Not by going to church", 
    "Not by being a good person",
    "Not by keeping commandments",
    "Not by confessing sins",
    "Not by praying prayers",
    "Not by any religious ritual",
    "Not by turning from sin first",
  ],
  
  onlyByFaith: "Salvation is received ONE way: by believing in Jesus Christ. Not believing + something. Just believing. The moment you trust Christ, you are saved—completely, permanently, eternally."
};

const gospelContent = {
  sections: [
    {
      id: 'total-forgiveness',
      title: 'Total Forgiveness: Once For All',
      content: `The moment you believed, every sin—past, present, and future—was forgiven. Not partially. Not progressively. Completely.

"And you, who were dead in your trespasses and the uncircumcision of your flesh, God made alive together with him, having forgiven us ALL our trespasses, by canceling the record of debt that stood against us with its legal demands. This he set aside, nailing it to the cross." (Colossians 2:13-14 ESV)

"For by a single offering he has perfected for all time those who are being sanctified." (Hebrews 10:14 ESV)

Notice: ONE offering. PERFECTED. FOR ALL TIME. This isn't religious wishful thinking—this is the clear teaching of Scripture. Jesus didn't make a down payment on your forgiveness; He paid it in full.

When you sin now, you don't need to ask God to forgive you again. You already HAVE forgiveness (Ephesians 1:7). Instead, thank Him: "Father, thank You that I'm already forgiven. I rest in Your finished work."

**Key Anchors:**
• Ephesians 1:7 – "In him we HAVE redemption... the forgiveness of our trespasses"
• Hebrews 10:10 – "We HAVE BEEN sanctified... once for all"
• Colossians 2:13-14 – "Having forgiven us ALL our trespasses"`
    },
    {
      id: 'union-security',
      title: 'Union & Security: Never Distant',
      content: `You are not trying to get close to God. You are not working to maintain fellowship. You are permanently, irrevocably UNITED with Christ.

"But he who is joined to the Lord becomes one spirit with him." (1 Corinthians 6:17 ESV)

One spirit. Not two. Not sometimes connected. ONE. This union cannot be broken by your sin, your feelings, or your performance. It was accomplished by Christ and is maintained by Christ.

"In him you also, when you heard the word of truth, the gospel of your salvation, and believed in him, were sealed with the promised Holy Spirit, who is the guarantee of our inheritance until we acquire possession of it." (Ephesians 1:13-14 ESV)

You are SEALED. The Holy Spirit is God's guarantee—His down payment—that what He started, He will finish. Your security rests on God's faithfulness, not yours.

There is no "in and out" of fellowship. There is no distance to close. You are as close to God right now as you will ever be—because Christ brought you near once for all.

**Key Anchors:**
• 1 Corinthians 6:17 – "One spirit with him"
• Ephesians 1:13-14 – "Sealed with the promised Holy Spirit"
• Romans 8:38-39 – "Nothing can separate us from God's love"`
    },
    {
      id: 'grace-vs-law',
      title: 'Grace vs. Law: Why Law Excites Sin',
      content: `Here's a truth that shocks many Christians: the Law doesn't help you live holy—it actually stirs up sin.

"For while we were living in the flesh, our sinful passions, aroused by the law, were at work in our members to bear fruit for death. But now we are released from the law, having died to that which held us captive, so that we serve in the new way of the Spirit and not in the old way of the written code." (Romans 7:5-6 ESV)

Did you catch that? Sinful passions AROUSED BY THE LAW. The Law tells you what not to do, and something in the flesh rises up in rebellion. This is why rule-keeping Christians often struggle more than grace-trusting Christians.

"For sin will have no dominion over you, since you are not under law but under grace." (Romans 6:14 ESV)

The solution to sin is not more rules—it's more grace. Grace doesn't produce license; grace produces genuine transformation:

"For the grace of God has appeared, bringing salvation for all people, training us to renounce ungodliness and worldly passions, and to live self-controlled, upright, and godly lives." (Titus 2:11-12 ESV)

Grace TRAINS us. Not guilt. Not fear. Not law. Grace itself produces holiness.

**Key Anchors:**
• Romans 7:5-6 – "Sinful passions aroused by the law... released from the law"
• Romans 6:14 – "Not under law but under grace"
• Titus 2:11-12 – "Grace... training us"`
    },
    {
      id: 'new-covenant-reality',
      title: 'New Covenant Reality: Everything Changed',
      content: `Jesus didn't come to tweak the Old Covenant. He came to establish something entirely new.

"In speaking of a new covenant, he makes the first one obsolete. And what is becoming obsolete and growing old is ready to vanish away." (Hebrews 8:13 ESV)

The Old Covenant—including the Ten Commandments as a rule of life—is OBSOLETE for believers. We are not under law but under grace (Romans 6:14). We are not led by rules but by the Spirit (Galatians 5:18).

This doesn't mean we live lawlessly. It means we live by a higher power—the indwelling Christ. "It is no longer I who live, but Christ who lives in me" (Galatians 2:20).

Under the Old Covenant, people had to repeatedly sacrifice for sins. Under the New Covenant, one sacrifice accomplished everything forever (Hebrews 10:10-14).

Under the Old Covenant, the Spirit came upon people temporarily. Under the New Covenant, the Spirit lives IN you permanently (1 Corinthians 6:19).

Under the Old Covenant, people had hearts of stone. Under the New Covenant, God gave you a new heart—a heart that wants to please Him (Ezekiel 36:26-27).

**Key Anchors:**
• Hebrews 8:13 – "The first [covenant] obsolete"
• Hebrews 9:15 – "He is the mediator of a new covenant"
• 2 Corinthians 3:6 – "Ministers of a new covenant, not of the letter but of the Spirit"`
    },
    {
      id: 'assurance',
      title: 'Unshakeable Assurance: Rest in the Finished Work',
      content: `Biblical assurance is not hoping you're saved. It's KNOWING you're saved because of what Christ accomplished—not because of how you're performing.

"I write these things to you who believe in the name of the Son of God, that you may KNOW that you have eternal life." (1 John 5:13 ESV)

Your assurance rests on:

**1. The Finished Work** - Jesus said "It is finished" (John 19:30). Not "It is started." Not "It is mostly done." FINISHED.

**2. God's Promise** - "Whoever believes in him is not condemned" (John 3:18). Present tense. Already true.

**3. Your Union** - You are one spirit with Christ (1 Corinthians 6:17). He would have to reject Himself to reject you.

**4. The Seal** - The Holy Spirit guarantees your inheritance (Ephesians 1:13-14).

When doubts come, don't look at your performance—look at His promise. Don't measure your feelings—rest in His finished work.

You are clean, close, and complete in Christ. Not because of what you do, but because of what He did.

**Key Anchors:**
• John 19:30 – "It is finished"
• 1 John 5:13 – "That you may KNOW you have eternal life"
• Hebrews 10:14 – "Perfected for all time"`
    }
  ],
  keyTruths: [
    { title: 'Totally Forgiven', description: 'All sins—past, present, future—dealt with once for all', icon: CheckCircle },
    { title: 'Permanently United', description: 'One spirit with Christ, never distant, always close', icon: Heart },
    { title: 'Free from Law', description: 'Not under law but under grace—Spirit-led living', icon: Zap },
    { title: 'Eternally Secure', description: 'Sealed by the Spirit, guaranteed by God', icon: Lock },
  ]
};

export default function GospelHub() {
  return (
    <div className="min-h-[calc(100vh-64px)]" data-testid="gospel-hub">
      {/* Hero Section */}
      <section className="relative h-64 sm:h-80 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1635414727475-286f0e8e02fd?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Sunrise representing hope and new beginning"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hub-hero-overlay flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 w-full">
            <span className="inline-block px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm mb-3">
              Step 1 · Belief
            </span>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              The Gospel
            </h1>
            <p className="text-white/80 mt-2 max-w-2xl">
              Total forgiveness, permanent union, and freedom from the Law
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Tabs defaultValue="gospel" className="space-y-8">
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="gospel" data-testid="tab-gospel">The Gospel</TabsTrigger>
            <TabsTrigger value="teaching" data-testid="tab-teaching">Deep Dive</TabsTrigger>
            <TabsTrigger value="quiz" data-testid="tab-quiz">Quiz</TabsTrigger>
          </TabsList>

          {/* THE GOSPEL - What Saves From What */}
          <TabsContent value="gospel" className="space-y-8">
            {/* The Question */}
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-sm text-muted-foreground uppercase tracking-wide mb-2">The most important question</p>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-2">
                "{gospelIntro.question}"
              </h2>
              <p className="text-primary font-medium">— {gospelIntro.reference}</p>
            </div>

            {/* The Answer */}
            <Card className="max-w-3xl mx-auto bg-primary text-primary-foreground">
              <CardContent className="p-8 text-center">
                <p className="text-sm uppercase tracking-wide mb-3 opacity-80">The answer</p>
                <p className="font-serif text-2xl sm:text-3xl font-bold mb-3">
                  "{gospelIntro.answer}"
                </p>
                <p className="opacity-80">— {gospelIntro.answerRef}</p>
              </CardContent>
            </Card>

            {/* Saved FROM / BY / TO */}
            <div className="grid lg:grid-cols-3 gap-6">
              {gospelIntro.sections.map((section, sIndex) => (
                <Card key={section.title} className="animate-slide-up" style={{ animationDelay: `${sIndex * 100}ms` }}>
                  <CardHeader className="pb-3">
                    <CardTitle className="font-serif text-xl text-center">{section.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {section.points.map((point, pIndex) => (
                      <div key={pIndex} className="space-y-1">
                        <p className="font-medium text-foreground">{point.text}</p>
                        <p className="text-sm italic text-muted-foreground">"{point.verse}"</p>
                        <p className="text-xs text-primary font-medium">— {point.ref}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* NOT by Works */}
            <Card className="max-w-3xl mx-auto border-destructive/30 bg-destructive/5">
              <CardHeader>
                <CardTitle className="font-serif text-center text-destructive">
                  Salvation is NOT by...
                </CardTitle>
                <CardDescription className="text-center">
                  "Not a result of works, so that no one may boast." — Ephesians 2:9
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
                  {gospelIntro.notByWorks.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <span className="text-destructive font-bold">✕</span>
                      <span className="text-foreground/80">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* ONLY by Faith */}
            <Card className="max-w-3xl mx-auto bg-green-50 border-green-200">
              <CardHeader>
                <CardTitle className="font-serif text-center text-green-700">
                  Salvation is ONLY by Faith in Christ
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <p className="text-foreground/80 leading-relaxed">
                  {gospelIntro.onlyByFaith}
                </p>
                <div className="p-4 bg-white rounded-lg border border-green-200">
                  <p className="font-serif text-xl italic text-green-800">
                    "Whoever believes in him is not condemned."
                  </p>
                  <p className="text-green-600 font-medium mt-2">— John 3:18</p>
                </div>
                <div className="flex flex-wrap justify-center gap-2 pt-2">
                  {['No procedures', 'No steps', 'No rituals', 'Just faith', 'Just Christ'].map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Simple Gospel Statement */}
            <div className="max-w-2xl mx-auto text-center p-8 bg-muted/30 rounded-lg">
              <p className="text-lg text-foreground/80 leading-relaxed mb-4">
                The gospel is not advice about what you must do. It's an announcement about what Christ has done. 
                He lived the life you couldn't live. He died the death you deserved. He rose to give you life.
              </p>
              <p className="font-serif text-2xl font-bold text-primary">
                Believe in Him, and you are saved.
              </p>
              <p className="text-sm text-muted-foreground mt-4">
                That's it. That's the gospel. No additions. No conditions. Just Christ.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="teaching" className="space-y-12">
            {/* Introduction */}
            <div className="max-w-3xl">
              <p className="text-lg text-muted-foreground leading-relaxed">
                The gospel is not merely an entry point—it is everything. And under the New Covenant, 
                the gospel announces something radical: you are totally forgiven, permanently united with 
                Christ, free from the Law, and led by the Spirit. Not working toward these things. Already there.
              </p>
            </div>

            {/* Key Truths */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {gospelContent.keyTruths.map((truth) => {
                const Icon = truth.icon;
                return (
                  <Card key={truth.title} className="text-center">
                    <CardContent className="pt-6">
                      <div className="p-3 rounded-full bg-primary/10 w-fit mx-auto mb-3">
                        <Icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                      </div>
                      <h3 className="font-serif font-semibold mb-1">{truth.title}</h3>
                      <p className="text-sm text-muted-foreground">{truth.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Teaching Sections */}
            <div className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible className="space-y-4">
                {gospelContent.sections.map((section, index) => (
                  <AccordionItem 
                    key={section.id} 
                    value={section.id}
                    className="border rounded-lg px-6"
                  >
                    <AccordionTrigger className="hover:no-underline py-4">
                      <div className="flex items-center gap-3 text-left">
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                          {index + 1}
                        </span>
                        <span className="font-serif text-lg font-semibold">{section.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-6">
                      <div className="pl-11 prose prose-slate max-w-none">
                        {section.content.split('\n\n').map((paragraph, pIndex) => (
                          <p key={pIndex} className="text-foreground/80 leading-relaxed mb-4 last:mb-0 whitespace-pre-wrap">
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            {/* Summary */}
            <Card className="max-w-3xl mx-auto bg-primary/5 border-primary/20">
              <CardHeader>
                <CardTitle className="font-serif flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  The New Covenant Gospel in Brief
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-foreground/80">
                <p>
                  <strong>You ARE forgiven</strong> – All sins, once for all, at the cross. (Colossians 2:13-14)
                </p>
                <p>
                  <strong>You ARE united</strong> – One spirit with Christ, never distant. (1 Corinthians 6:17)
                </p>
                <p>
                  <strong>You ARE free</strong> – Released from law, led by the Spirit. (Romans 7:6; Galatians 5:18)
                </p>
                <p>
                  <strong>You ARE secure</strong> – Sealed by the Spirit, guaranteed forever. (Ephesians 1:13-14)
                </p>
                <p className="italic border-t pt-4 mt-4 border-border">
                  "It is finished." – Jesus (John 19:30)
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="quiz">
            <div className="max-w-2xl mx-auto">
              <QuizComponent hubId="gospel" />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
